import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import {
  AutoColorSwatchGrid,
  ColorPreviewCard,
} from "@/components/foundations/inputs/ColorPicker/ColorPicker";
import { ListColorKey, resolvePairedColorKeys } from "@/lib/listColors";

export type TaskColorDraft = {
  inheritView: boolean;
  barColor: ListColorKey | null;
  bgColor: ListColorKey | null;
};

type TaskDisplayControlsProps = {
  value: TaskColorDraft;
  onChange: (next: TaskColorDraft) => void;
  previewTitle: string;
  previewLabel: string;
  fallbackBar: string | null;
  fallbackBg: string | null;
  accent?: {
    bar: string;
    soft: string;
    ink: string;
  };
};

type Props = TaskDisplayControlsProps & {
  onCancel: () => void;
  onSave: () => void;
  isSaving?: boolean;
};

export function TaskDisplayControls({
  value,
  onChange,
  previewTitle,
  previewLabel,
  fallbackBar,
  fallbackBg,
  accent,
}: TaskDisplayControlsProps) {
  const switchStyle =
    accent && !value.inheritView ? { backgroundColor: accent.bar } : undefined;
  const displayColors = resolvePairedColorKeys({
    primaryColor: value.barColor,
    secondaryColor: value.bgColor,
    fallbackPrimary: fallbackBar,
    fallbackSecondary: fallbackBg,
  });

  return (
    <div className="ui-task-display-controls space-y-4">
      <Section accent={accent}>
        <div className="ui-task-display-controls__custom-toggle flex items-center justify-between gap-4">
          <div>
            <Label
              className="ui-task-display-controls__toggle-label cursor-pointer"
              style={accent ? { color: accent.ink } : undefined}
            >
              Custom display
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Default uses the list colors for this task.
            </p>
          </div>
          <Switch
            checked={!value.inheritView}
            style={switchStyle}
            onCheckedChange={customDisplay => {
              onChange({
                ...value,
                inheritView: !customDisplay,
              });
            }}
          />
        </div>
      </Section>

      {!value.inheritView && (
        <>
          <Section accent={accent} label="Primary color">
            <TaskPrimaryColorPicker
              value={value.barColor}
              onChange={barColor => onChange({ ...value, barColor })}
              autoColor={displayColors.secondary}
            />
          </Section>

          <Section accent={accent} label="Secondary color">
            <TaskBackgroundColorPicker
              value={value.bgColor}
              onChange={bgColor => onChange({ ...value, bgColor })}
              autoColor={displayColors.primary}
            />
          </Section>
        </>
      )}

      <ColorPreviewCard
        barColor={value.inheritView ? null : value.barColor}
        bgColor={value.inheritView ? null : value.bgColor}
        fallbackBar={fallbackBar}
        fallbackBg={fallbackBg}
        title={previewTitle.trim() || "Sample task"}
        label={previewLabel}
      />
    </div>
  );
}

export function TaskColorEditor({
  value,
  onChange,
  onCancel,
  onSave,
  isSaving = false,
  previewTitle,
  previewLabel,
  fallbackBar,
  fallbackBg,
  accent,
}: Props) {
  return (
    <div className="ui-task-display-editor space-y-4">
      <TaskDisplayControls
        value={value}
        onChange={onChange}
        previewTitle={previewTitle}
        previewLabel={previewLabel}
        fallbackBar={fallbackBar}
        fallbackBg={fallbackBg}
        accent={accent}
      />

      <div className="ui-task-display-editor__actions grid grid-cols-2 gap-3">
        <Button
          type="button"
          variant="ghost"
          className="h-12 bg-muted/60 hover:bg-muted"
          onClick={onCancel}
          disabled={isSaving}
          style={accent ? { color: accent.ink } : undefined}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="h-12"
          onClick={onSave}
          disabled={isSaving}
          style={accent ? { background: accent.bar, color: accent.soft } : undefined}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function TaskPrimaryColorPicker({
  value,
  onChange,
  autoColor,
}: {
  value: ListColorKey | null;
  onChange: (key: ListColorKey | null) => void;
  autoColor: string | null | undefined;
}) {
  return (
    <AutoColorSwatchGrid
      value={value}
      onChange={onChange}
      variant="bar"
      auto={{
        label: "Auto",
        ariaLabel: "Auto primary",
        title: "Auto (matches secondary)",
        color: autoColor,
      }}
    />
  );
}

function TaskBackgroundColorPicker({
  value,
  onChange,
  autoColor,
}: {
  value: string | null | undefined;
  onChange: (key: ListColorKey | null) => void;
  autoColor: string | null | undefined;
}) {
  return (
    <AutoColorSwatchGrid
      value={value}
      onChange={onChange}
      variant="soft"
      auto={{
        label: "Auto",
        ariaLabel: "Auto secondary",
        title: "Auto (matches primary)",
        color: autoColor,
      }}
    />
  );
}

function Section({
  children,
  accent,
  label,
  onReset,
  resetLabel,
  disabled = false,
}: {
  children: React.ReactNode;
  accent?: { bar: string; soft: string; ink: string };
  label?: string;
  onReset?: () => void;
  resetLabel?: string;
  disabled?: boolean;
}) {
  return (
    <section
      className={`ui-task-display-section bg-card border rounded-xl p-4 space-y-2 transition-opacity ${
        disabled ? "opacity-45" : ""
      }`}
      style={
        accent
          ? { borderColor: `color-mix(in oklch, ${accent.bar} 18%, transparent)` }
          : undefined
      }
    >
      {(label || onReset) && (
        <div className="ui-task-display-section__header flex items-center justify-between">
          {label ? (
            <div
              className="ui-task-display-section__label text-[11px] uppercase tracking-wider text-muted-foreground"
              style={accent ? { color: accent.ink, opacity: 0.68 } : undefined}
            >
              {label}
            </div>
          ) : (
            <span />
          )}
          {onReset && !disabled && (
            <button
              type="button"
              onClick={onReset}
              className="ui-task-display-section__reset inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              style={accent ? { color: accent.ink } : undefined}
              aria-label={resetLabel ?? "Reset color"}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      <div className={`ui-task-display-section__body ${disabled ? "pointer-events-none" : ""}`}>
        {children}
      </div>
    </section>
  );
}
